"use client"

import { useRef, useState, useEffect } from "react"
import { Input } from "../ui/input"
import { Button } from "../ui/button"
import { toast } from "sonner"
import { post } from "../../lib/api"
import { readFileWithEncodingDetection } from "../../lib/encoding-utils"

// InfoOverlay component (moved outside to avoid closure issues)
const InfoOverlay = ({ open, onClose }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 ">
            <div className="bg-[#ffffff] rounded-lg shadow-lg max-w-lg w-full p-6 relative dark:bg-[#2c313c] dark:text-white">
                <button
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl font-bold"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <svg width="22" height="22" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="256" cy="256" r="216" stroke="black" strokeWidth="32" fill="none"/>
                        <rect x="232" y="200" width="48" height="160" rx="24" fill="black"/>
                        <circle cx="256" cy="144" r="24" fill="black"/>
                    </svg>
                    How does the uploader work?
                </h2>
                <div className="space-y-3 text-gray-800 text-sm dark:text-white">
                    <p>
                        Upload a <b>.txt file</b> with 8-letter words, one per line, or enter a word manually. The uploader will only accept valid 8-letter words (A-Z).
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Click the blue button to upload a .txt file, or type a word manually and click Load Word.</li>
                        <li>Each word must be exactly 8 letters, no spaces or numbers.</li>
                        <li>After loading, you can preview, schedule, and upload the puzzle.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

const HorseJumpPlanning = ({ game_id, onUpdate }) => {
    const [words, setWords] = useState([])
    const [currentWordIndex, setCurrentWordIndex] = useState(0)
    const [currentPathIndex, setCurrentPathIndex] = useState(0)
    const [allPaths, setAllPaths] = useState([])
    const [manualWord, setManualWord] = useState("")
    const [isManualMode, setIsManualMode] = useState(false)
    const [scheduledDate, setScheduledDate] = useState("")
    const [lastUploadedDate, setLastUploadedDate] = useState(null)
    const fileInputRef = useRef(null)
    const [showInfo, setShowInfo] = useState(false)

    // Initialize with today's date
    useEffect(() => {
        const today = new Date()
        const todayString = today.toISOString().split("T")[0]
        setScheduledDate(todayString)
    }, [])

    // Update default date after upload
    useEffect(() => {
        if (lastUploadedDate) {
            const nextDay = new Date(lastUploadedDate)
            nextDay.setDate(nextDay.getDate() + 1)
            const nextDayString = nextDay.toISOString().split("T")[0]
            setScheduledDate(nextDayString)
        }
    }, [lastUploadedDate])

    // 3x3 grid positions (middle is empty)
    // 0 1 2
    // 3 X 4
    // 5 6 7
    const positions = [
        [0, 0],
        [0, 1],
        [0, 2], // 0, 1, 2
        [1, 0],
        [1, 2], // 3, 4
        [2, 0],
        [2, 1],
        [2, 2], // 5, 6, 7
    ]

    // Knight moves: [row_offset, col_offset]
    const knightMoves = [
        [-2, -1],
        [-2, 1],
        [-1, -2],
        [-1, 2],
        [1, -2],
        [1, 2],
        [2, -1],
        [2, 1],
    ]

    const getRandomPathIndex = () => {
        return Math.floor(Math.random() * allPaths.length)
    }

    // Find all valid knight tour paths
    useEffect(() => {
        const findAllPaths = () => {
            const paths = []

            const getValidMoves = (posIndex) => {
                if (posIndex < 0 || posIndex >= 8) return []

                const currentPos = positions[posIndex]
                const validMoves = []

                for (const [dr, dc] of knightMoves) {
                    const newRow = currentPos[0] + dr
                    const newCol = currentPos[1] + dc

                    // Check if new position is valid and not the center
                    if (newRow >= 0 && newRow <= 2 && newCol >= 0 && newCol <= 2 && !(newRow === 1 && newCol === 1)) {
                        // Find the index of this position
                        const newIndex = positions.findIndex((pos) => pos[0] === newRow && pos[1] === newCol)
                        if (newIndex !== -1) {
                            validMoves.push(newIndex)
                        }
                    }
                }

                return validMoves
            }

            const dfs = (path, visited) => {
                if (path.length === 8) {
                    paths.push([...path])
                    return
                }

                const currentPos = path[path.length - 1]
                const validMoves = getValidMoves(currentPos)

                for (const nextPos of validMoves) {
                    if (!visited.has(nextPos)) {
                        path.push(nextPos)
                        visited.add(nextPos)
                        dfs(path, visited)
                        path.pop()
                        visited.delete(nextPos)
                    }
                }
            }

            // Try starting from each position
            for (let startPos = 0; startPos < 8; startPos++) {
                dfs([startPos], new Set([startPos]))
            }

            return paths
        }

        setAllPaths(findAllPaths())
    }, [])

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file || file.type !== "text/plain") {
            toast.error("Please upload a valid .txt file.")
            return
        }

        try {
            const text = await readFileWithEncodingDetection(file)
            const lines = text
                .split("\n")
                .map((line) => line.trim().toUpperCase())
                .filter((line) => line.length === 8 && /^[A-Z]+$/.test(line))

            if (lines.length === 0) {
                toast.error("No valid 8-letter words found in the file.")
                return
            }

            setWords(lines)
            setCurrentWordIndex(0)
            setCurrentPathIndex(getRandomPathIndex())
            setIsManualMode(false)
            toast.success(`Loaded ${lines.length} valid 8-letter words!`)
        } catch (error) {
            toast.error("Failed to read file. Please try again.")
            console.error("File reading error:", error)
        }
    }

    const handleManualWordSubmit = () => {
        const word = manualWord.trim().toUpperCase()

        if (word.length !== 8) {
            toast.error("Please enter exactly 8 letters!")
            return
        }

        if (!/^[A-Z]+$/.test(word)) {
            toast.error("Please enter only letters!")
            return
        }

        setWords([word])
        setCurrentWordIndex(0)
        setCurrentPathIndex(getRandomPathIndex())
        setIsManualMode(true)
        toast.success(`Word "${word}" loaded successfully!`)
    }

    const getCurrentWord = () => words[currentWordIndex] || ""

    const getCurrentPuzzle = () => {
        if (!getCurrentWord() || allPaths.length === 0) return ""

        const currentPath = allPaths[currentPathIndex]
        const word = getCurrentWord()

        // Create puzzle by arranging letters according to the path
        const puzzle = new Array(8)
        for (let i = 0; i < 8; i++) {
            puzzle[currentPath[i]] = word[i]
        }

        return puzzle.join("")
    }

    const getGridDisplay = () => {
        if (!getCurrentWord() || allPaths.length === 0) {
            return Array(9).fill("")
        }

        const currentPath = allPaths[currentPathIndex]
        const word = getCurrentWord()
        const grid = Array(9).fill("")

        // Fill grid positions (skip index 4 which is center)
        for (let i = 0; i < 8; i++) {
            const gridIndex = currentPath[i] < 4 ? currentPath[i] : currentPath[i] + 1
            grid[gridIndex] = word[i]
        }

        grid[4] = "" // Center - empty for now, can be replaced with horse image
        return grid
    }

    const nextWord = () => {
        if (currentWordIndex < words.length - 1) {
            setCurrentWordIndex(currentWordIndex + 1)
            setCurrentPathIndex(getRandomPathIndex())
        }
    }

    const prevWord = () => {
        if (currentWordIndex > 0) {
            setCurrentWordIndex(currentWordIndex - 1)
            setCurrentPathIndex(getRandomPathIndex())
        }
    }

    const formatDateForDisplay = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    const handleUploadPuzzle = async () => {
        const word = getCurrentWord()
        const puzzle = getCurrentPuzzle()

        if (!word || !puzzle) {
            toast.error("No valid puzzle to upload.")
            return
        }

        if (!scheduledDate) {
            toast.error("Please select a date for the challenge.")
            return
        }

        console.log("game_id", game_id)
        console.log("word", word)
        console.log("puzzle", puzzle)
        console.log("scheduledDate", scheduledDate)

        try {
            // First API call - Add the challenge
            const response = await post(
                {
                    method: "addChallengeNoDup",
                    game_id: game_id,
                    challenge_id: word, // Solution is the challenge name
                    data: puzzle, // Scrambled letters is the challenge data
                    variant_id: 1,
                    variant_name: "challenge",
                },
                "/challenges",
            )

            if (response.error) {
                if (response.error === "Failed to fetch") {
                    toast.error("A challenge with that name already exists.")
                } else {
                    toast.error(response.error)
                }
                return
            }

            // Second API call - Schedule the daily challenge
            const dailyResponse = await post(
                {
                    method: "addDailyChallenge",
                    game_id: game_id,
                    date: scheduledDate,
                    daily_challenge_info: word, // Use the word (solution) as the daily challenge info
                },
                "/challenges",
            )

            if (dailyResponse.error) {
                toast.error(`Challenge uploaded but scheduling failed: ${dailyResponse.error}`)
                return
            }

            toast.success(`Puzzle "${word}" uploaded and scheduled for ${formatDateForDisplay(scheduledDate)}!`)
            setLastUploadedDate(scheduledDate) // Update the last uploaded date
            onUpdate?.()
        } catch (err) {
            toast.error("Upload failed!")
            console.error(err)
        }
    }

    const gridDisplay = getGridDisplay()

    return (
        <>
            <div className="space-y-4">
                {/* Manual Word Entry */}
                <div className="flex items-center gap-3 mb-2">
                <Input type="file" accept=".txt" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                    <Button
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white border border-blue-700 dark:border-blue-700 px-6 py-3 font-semibold shadow-md"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload 8-Letter Words File
                    </Button>
                    <span className="text-gray-500 dark:text-gray-400">or</span>
                    <Input
                        type="text"
                        placeholder="Enter 8-letter word manually"
                        value={manualWord}
                        onChange={(e) => setManualWord(e.target.value.toUpperCase())}
                        maxLength={8}
                        className="max-w-xs text-gray-900 dark:text-white dark:bg-gray-800 dark:placeholder-gray-500 dark:border-gray-600 placeholder-gray-500 border border-gray-300 dark:border-gray-600"
                        onKeyPress={(e) => e.key === "Enter" && handleManualWordSubmit()}
                    />
                    <Button
                        className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white border border-green-700 dark:border-green-700 px-6 py-3 font-semibold shadow-md"
                        onClick={handleManualWordSubmit}
                    >
                        Load Word
                    </Button>
                </div>
            </div>

            {words.length > 0 && (
                <>
                    {/* Word Navigation - Only show if not in manual mode or if manual mode has multiple words */}
                    {(!isManualMode || words.length > 1) && (
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
                            <Button
                                variant="outline"
                                className="border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white px-4 py-2 font-medium shadow-sm"
                                onClick={prevWord}
                                disabled={currentWordIndex === 0}
                            >
                                Previous Word
                            </Button>
                            <div className="text-center">
                                <div className="font-bold text-lg text-gray-900 dark:text-white">
                                    Word {currentWordIndex + 1} of {words.length}: {getCurrentWord()}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    Solution: {getCurrentWord()} | Puzzle: {getCurrentPuzzle()}
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                className="border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white px-4 py-2 font-medium shadow-sm"
                                onClick={nextWord}
                                disabled={currentWordIndex === words.length - 1}
                            >
                                Next Word
                            </Button>
                        </div>
                    )}

                    {/* Manual mode single word display */}
                    {isManualMode && words.length === 1 && (
                        <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
                            <div className="font-bold text-lg text-gray-900 dark:text-white">Current Word: {getCurrentWord()}</div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">
                                Solution: {getCurrentWord()} | Puzzle: {getCurrentPuzzle()}
                            </div>
                        </div>
                    )}

                    {/* Grid Display */}
                    <div className="flex justify-center py-6 mb-2">
                        <div className="grid grid-cols-3 gap-4 p-8 bg-[#ffffff] dark:bg-[#2c313c] border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                            {gridDisplay.map((letter, index) => (
                                <div
                                    key={index}
                                    className={`
          w-20 h-20 flex items-center justify-center text-2xl font-bold border-2 rounded-lg shadow-sm
          ${
                                        index === 4
                                            ? "bg-amber-100 dark:bg-yellow-900 border-amber-300 dark:border-yellow-900 relative"
                                            : letter
                                                ? "bg-blue-100 dark:bg-blue-900 border-blue-300 dark:border-blue-900 text-blue-800 dark:text-blue-300"
                                                : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500"
                                    }
        `}
                                >
                                    {index === 4 ? (
                                        // Center square - you can replace this with an image
                                        <div className="text-amber-600 dark:text-yellow-200 text-4xl">♞</div>
                                        // To use an image instead, uncomment below and provide the image:
                                        // <img src="/path-to-your-horse-image.png" alt="Chess Horse" className="w-12 h-12" />
                                    ) : (
                                        letter
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Puzzle Info */}
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-900 rounded-lg mb-2">
                        <div className="text-base text-gray-900 dark:text-white space-y-1">
                            <div>
                                <strong>Original Word:</strong> {getCurrentWord()}
                            </div>
                            <div>
                                <strong>Puzzle Format:</strong> {getCurrentPuzzle()}
                            </div>
                        </div>
                    </div>

                    {/* Date Scheduling */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-900 rounded-lg mb-2">
                        <div className="space-y-3">
                            <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-900 dark:text-white">
                                Schedule Challenge Date:
                            </label>
                            <div className="flex items-center gap-4">
                                <Input
                                    type="date"
                                    id="scheduledDate"
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.target.value)}
                                    className="max-w-xs text-gray-900 dark:text-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600"
                                />
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    {scheduledDate && `Will be scheduled for: ${formatDateForDisplay(scheduledDate)}`}
                                </div>
                            </div>
                            {lastUploadedDate && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Last upload was scheduled for {formatDateForDisplay(lastUploadedDate)}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Upload Button */}
                    <Button
                        className="w-full bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white border border-green-700 dark:border-green-700 py-3 font-semibold shadow-md mb-2"
                        onClick={handleUploadPuzzle}
                    >
                        Schedule This Puzzle for {scheduledDate ? formatDateForDisplay(scheduledDate) : "Selected Date"}
                    </Button>
                </>
            )}

            {words.length === 0 && (
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-md mb-4 mt-4">
                    <span>
                        📁 Upload a .txt file with 8-letter words or enter a word manually to get started
                    </span>
                    <button
                        className="ml-2 p-1 rounded-full flex items-center justify-center"
                        onClick={() => setShowInfo(true)}
                        aria-label="How does the uploader work?"
                        type="button"
                    >
                        <svg width="20" height="20" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="256" cy="256" r="216" stroke="currentColor" strokeWidth="40" fill="none"/>
                            <rect x="232" y="200" width="48" height="160" rx="24" fill="currentColor"/>
                            <circle cx="256" cy="144" r="24" fill="currentColor"/>
                        </svg>
                    </button>
                </div>
            )}
            <InfoOverlay open={showInfo} onClose={() => setShowInfo(false)} />
        </>
    )
}

export default HorseJumpPlanning 